import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCreatorEarnings } from '../services/earningsService';
import './CreatorEarningsPage.css';

const USER_TYPE_LABELS = {
  super_admin:     'Super Admin',
  content_creator: 'Creator',
  general_user:    'User',
};

const isTestAccount = (p) =>
  p.buyer_user_type === 'super_admin' || p.buyer_user_type === 'content_creator';

const fmt = (n) => Number(n || 0).toFixed(0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ── Chevron icon ───────────────────────────────────────────────────────────
const Chevron = ({ open }) => (
  <svg
    className={`ce-chevron${open ? ' ce-chevron--open' : ''}`}
    width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Expanded purchases sub-table ───────────────────────────────────────────
const CreatorPurchasesRow = ({ colSpan, purchases }) => (
  <tr className="ce-expanded-row">
    <td colSpan={colSpan} className="ce-expanded-cell">
      {purchases.length === 0 ? (
        <p className="ce-expanded-empty">No purchases yet.</p>
      ) : (
        <table className="ce-sub-table">
          <thead>
            <tr>
              <th>Buyer</th>
              <th>Type</th>
              <th>Post</th>
              <th>Credits Paid</th>
              <th>Creator Cut</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p, i) => (
              <tr
                key={p.access_id}
                className={[
                  i % 2 === 0 ? 'row-even' : 'row-odd',
                  isTestAccount(p) ? 'ce-row-test' : '',
                ].join(' ')}
              >
                <td className="ce-td-creator">
                  <span className={`ce-avatar ce-avatar--sm${isTestAccount(p) ? ' ce-avatar-test' : ''}`}>
                    {(p.buyer_username || '?')[0].toUpperCase()}
                  </span>
                  <div className="ce-creator-info">
                    <span className="ce-creator-name">@{p.buyer_username}</span>
                    <span className="ce-creator-username">{p.buyer_email}</span>
                  </div>
                </td>
                <td>
                  <span className={`ce-type-badge ce-type-${p.buyer_user_type}`}>
                    {USER_TYPE_LABELS[p.buyer_user_type] || p.buyer_user_type}
                  </span>
                </td>
                <td className="ce-td-post">{p.post_title || `#${p.post_id}`}</td>
                <td className="ce-td-total">{p.credits_spent}</td>
                <td className="ce-td-count">{fmt(p.creator_cut)}</td>
                <td className="ce-td-email">{fmtDate(p.purchased_at)}</td>
                <td>
                  <span className={`ce-badge ce-badge-${p.payout_status}`}>
                    {p.payout_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </td>
  </tr>
);

// ── Main page ──────────────────────────────────────────────────────────────
const CreatorEarningsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedCreators, setExpandedCreators] = useState(new Set());

  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('all');

  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [purchaseFilter, setPurchaseFilter] = useState('all');

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.user_type !== 'super_admin') navigate('/dashboard');
  }, [user, navigate]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await getCreatorEarnings());
    } catch {
      setError('Failed to load earnings data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.user_type === 'super_admin') fetchEarnings();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCreator = (id) => {
    setExpandedCreators((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (!user || user.user_type !== 'super_admin') {
    return (
      <div className="ce-container">
        <div className="ce-access-denied"><h2>Access Denied</h2></div>
      </div>
    );
  }

  const filteredCreators = (data?.creators || []).filter((c) => {
    const matchesSearch =
      !creatorSearch ||
      c.username?.toLowerCase().includes(creatorSearch.toLowerCase()) ||
      c.email?.toLowerCase().includes(creatorSearch.toLowerCase()) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(creatorSearch.toLowerCase());
    const matchesFilter =
      creatorFilter === 'all' ||
      (creatorFilter === 'pending' && c.pending_amount > 0) ||
      (creatorFilter === 'paid'    && c.paid_amount    > 0);
    return matchesSearch && matchesFilter;
  });

  const filteredPurchases = (data?.purchases || []).filter((p) => {
    const matchesSearch =
      !purchaseSearch ||
      p.buyer_username?.toLowerCase().includes(purchaseSearch.toLowerCase()) ||
      p.buyer_email?.toLowerCase().includes(purchaseSearch.toLowerCase()) ||
      p.post_title?.toLowerCase().includes(purchaseSearch.toLowerCase()) ||
      p.creator_username?.toLowerCase().includes(purchaseSearch.toLowerCase());
    const matchesFilter =
      purchaseFilter === 'all' ||
      (purchaseFilter === 'real' && !isTestAccount(p)) ||
      (purchaseFilter === 'test' &&  isTestAccount(p));
    return matchesSearch && matchesFilter;
  });

  const purchasesForCreator = (creatorId) =>
    (data?.purchases || []).filter((p) => p.creator_id === creatorId);

  const realCount = filteredPurchases.filter((p) => !isTestAccount(p)).length;
  const testCount = filteredPurchases.filter((p) =>  isTestAccount(p)).length;

  return (
    <div className="ce-container">
      {/* Hero */}
      <div className="ce-hero">
        <div className="ce-hero-content">
          <div className="ce-hero-left">
            <h1>Creator Earnings</h1>
            <p className="ce-hero-subtitle">Track credit payouts to content creators</p>
          </div>
          <button className="ce-back-btn" onClick={() => navigate('/dashboard/super-admin')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="ce-body">
        {/* Summary cards */}
        {data && (
          <div className="ce-stats-grid">
            <div className="ce-stat-card ce-stat-total">
              <div className="ce-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div className="ce-stat-info">
                <span className="ce-stat-label">Total Earnings</span>
                <span className="ce-stat-value">{fmt(data.summary.total_earnings)} credits</span>
              </div>
            </div>
            <div className="ce-stat-card ce-stat-pending">
              <div className="ce-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="ce-stat-info">
                <span className="ce-stat-label">Pending Payout</span>
                <span className="ce-stat-value">{fmt(data.summary.pending_payout)} credits</span>
              </div>
            </div>
            <div className="ce-stat-card ce-stat-paid">
              <div className="ce-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="ce-stat-info">
                <span className="ce-stat-label">Paid Out</span>
                <span className="ce-stat-value">{fmt(data.summary.paid_payout)} credits</span>
              </div>
            </div>
            <div className="ce-stat-card ce-stat-purchases">
              <div className="ce-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </div>
              <div className="ce-stat-info">
                <span className="ce-stat-label">Total Purchases</span>
                <span className="ce-stat-value">{data.summary.total_purchases}</span>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="ce-loading"><div className="ce-spinner" /><p>Loading earnings...</p></div>
        ) : error ? (
          <div className="ce-error"><p>{error}</p><button className="ce-retry-btn" onClick={fetchEarnings}>Try Again</button></div>
        ) : (
          <>
            {/* ── By Creator ──────────────────────────────────────────── */}
            <div className="ce-section-header">
              <h2 className="ce-section-title">By Creator</h2>
              <p className="ce-section-subtitle">Click a row to see individual purchases</p>
            </div>

            <div className="ce-toolbar">
              <div className="ce-search-wrapper">
                <svg className="ce-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input className="ce-search-input" type="text" placeholder="Search creator..." value={creatorSearch} onChange={(e) => setCreatorSearch(e.target.value)} />
                {creatorSearch && (
                  <button className="ce-search-clear" onClick={() => setCreatorSearch('')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                )}
              </div>
              <div className="ce-filter-tabs">
                {['all', 'pending', 'paid'].map((f) => (
                  <button key={f} className={`ce-filter-tab${creatorFilter === f ? ' active' : ''}`} onClick={() => setCreatorFilter(f)}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              <span className="ce-count">{filteredCreators.length} creator{filteredCreators.length !== 1 ? 's' : ''}</span>
            </div>

            {filteredCreators.length === 0 ? (
              <div className="ce-empty"><p>No creators found.</p></div>
            ) : (
              <div className="ce-table-wrapper">
                <table className="ce-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} />
                      <th>Creator</th>
                      <th>Email</th>
                      <th>Purchases</th>
                      <th>Total Earned</th>
                      <th>Pending</th>
                      <th>Paid Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCreators.map((c, i) => {
                      const isOpen = expandedCreators.has(c.creator_id);
                      const creatorPurchases = purchasesForCreator(c.creator_id);
                      return (
                        <React.Fragment key={c.creator_id}>
                          <tr
                            className={`ce-creator-row${isOpen ? ' ce-creator-row--open' : ''} ${i % 2 === 0 ? 'row-even' : 'row-odd'}`}
                            onClick={() => toggleCreator(c.creator_id)}
                          >
                            <td className="ce-chevron-cell">
                              <Chevron open={isOpen} />
                            </td>
                            <td className="ce-td-creator">
                              <span className="ce-avatar">{(c.username || '?')[0].toUpperCase()}</span>
                              <div className="ce-creator-info">
                                <span className="ce-creator-name">{c.first_name} {c.last_name}</span>
                                <span className="ce-creator-username">@{c.username}</span>
                              </div>
                            </td>
                            <td className="ce-td-email">{c.email}</td>
                            <td className="ce-td-count">{c.purchase_count}</td>
                            <td className="ce-td-total">{fmt(c.total_earnings)}</td>
                            <td>
                              {c.pending_amount > 0
                                ? <span className="ce-badge ce-badge-pending">{fmt(c.pending_amount)}</span>
                                : <span className="ce-badge ce-badge-zero">—</span>}
                            </td>
                            <td>
                              {c.paid_amount > 0
                                ? <span className="ce-badge ce-badge-paid">{fmt(c.paid_amount)}</span>
                                : <span className="ce-badge ce-badge-zero">—</span>}
                            </td>
                          </tr>

                          {isOpen && (
                            <CreatorPurchasesRow
                              colSpan={7}
                              purchases={creatorPurchases}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── All Purchases ───────────────────────────────────────── */}
            <div className="ce-section-header ce-section-header--purchases">
              <h2 className="ce-section-title">All Purchases</h2>
              <p className="ce-section-subtitle">
                {realCount} real customer purchase{realCount !== 1 ? 's' : ''} · {testCount} test
              </p>
            </div>

            <div className="ce-toolbar">
              <div className="ce-search-wrapper">
                <svg className="ce-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input className="ce-search-input" type="text" placeholder="Search buyer, post or creator..." value={purchaseSearch} onChange={(e) => setPurchaseSearch(e.target.value)} />
                {purchaseSearch && (
                  <button className="ce-search-clear" onClick={() => setPurchaseSearch('')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                )}
              </div>
              <div className="ce-filter-tabs">
                {[
                  { key: 'all',  label: 'All' },
                  { key: 'real', label: 'Real customers' },
                  { key: 'test', label: 'Test accounts' },
                ].map(({ key, label }) => (
                  <button key={key} className={`ce-filter-tab${purchaseFilter === key ? ' active' : ''}`} onClick={() => setPurchaseFilter(key)}>
                    {label}
                  </button>
                ))}
              </div>
              <span className="ce-count">{filteredPurchases.length} purchase{filteredPurchases.length !== 1 ? 's' : ''}</span>
            </div>

            {filteredPurchases.length === 0 ? (
              <div className="ce-empty"><p>No purchases found.</p></div>
            ) : (
              <div className="ce-table-wrapper">
                <table className="ce-table">
                  <thead>
                    <tr>
                      <th>Buyer</th>
                      <th>Type</th>
                      <th>Post</th>
                      <th>Creator</th>
                      <th>Credits Paid</th>
                      <th>Creator Cut</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchases.map((p, i) => (
                      <tr
                        key={p.access_id}
                        className={[
                          i % 2 === 0 ? 'row-even' : 'row-odd',
                          isTestAccount(p) ? 'ce-row-test' : '',
                        ].join(' ')}
                      >
                        <td className="ce-td-creator">
                          <span className={`ce-avatar${isTestAccount(p) ? ' ce-avatar-test' : ''}`}>
                            {(p.buyer_username || '?')[0].toUpperCase()}
                          </span>
                          <div className="ce-creator-info">
                            <span className="ce-creator-name">@{p.buyer_username}</span>
                            <span className="ce-creator-username">{p.buyer_email}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`ce-type-badge ce-type-${p.buyer_user_type}`}>
                            {USER_TYPE_LABELS[p.buyer_user_type] || p.buyer_user_type}
                          </span>
                        </td>
                        <td className="ce-td-post">{p.post_title || `#${p.post_id}`}</td>
                        <td className="ce-td-email">@{p.creator_username}</td>
                        <td className="ce-td-total">{p.credits_spent}</td>
                        <td className="ce-td-count">{fmt(p.creator_cut)}</td>
                        <td className="ce-td-email">{fmtDate(p.purchased_at)}</td>
                        <td>
                          <span className={`ce-badge ce-badge-${p.payout_status}`}>
                            {p.payout_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreatorEarningsPage;
